import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PendingSelection, setPendingSelection } from '../utils/choosePlan';
import './PricingBilling.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface CatalogFeature {
  featureKey: string;
  label: string;
  unit: string;
  group: string;
  bundleEligible: boolean;
}

interface CatalogPaygItem extends CatalogFeature {
  pricePerUnit: number;
  currency?: 'INR' | 'USD';
  price?: number;
  maxPerMonth: number | null;
  updatedAt: string;
}

interface CatalogPlanLimit extends CatalogFeature {
  maxPerMonth: number | null;
}

interface CatalogPlan {
  id: string;
  name: string;
  priceMonthly: number;
  currency?: 'INR' | 'USD';
  price?: number;
  status: 'active' | 'archived';
  sortOrder: number;
  isRecommended?: boolean;
  limits: CatalogPlanLimit[];
}

interface PricingCatalogResponse {
  boundaryTimezone: 'UTC';
  features: CatalogFeature[];
  payg: CatalogPaygItem[];
  plans: CatalogPlan[];
}

const formatPrice = (value: number) => `$${value.toFixed(2)}`;
const formatPlanPrice = (plan: CatalogPlan) =>
  plan.currency === 'INR' ? `₹${(plan.price ?? plan.priceMonthly).toFixed(2)}` : formatPrice(plan.price ?? plan.priceMonthly);

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [catalog, setCatalog] = useState<PricingCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/pricing/catalog`, { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`Failed to load catalog (${response.status})`);
        }
        const data: PricingCatalogResponse = await response.json();
        if (!cancelled) {
          setCatalog(data);
        }
      } catch {
        if (!cancelled) {
          setError('Unable to load pricing catalog right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Remember the pick, then sign in (or go straight to the prompt when already signed in).
  const selectOption = (selection: PendingSelection) => {
    setPendingSelection(selection);
    navigate(isAuthenticated ? '/choose-plan' : '/login');
  };

  return (
    <div className="pricing-shell">
      <div className="pricing-hero">
        <h1 className="pricing-title">Pricing</h1>
        <p className="pricing-subtitle">
          Compare subscription plans and PAYG rates. Billing period boundary is UTC.
        </p>
        <div className="billing-actions">
          <Link to="/login" className="billing-btn secondary">Sign In</Link>
          <Link to="/account/billing" className="billing-btn">Go to Billing</Link>
        </div>
      </div>

      {loading && <div className="pricing-loading">Loading pricing catalog...</div>}
      {error && <div className="pricing-error">{error}</div>}

      {!loading && !error && catalog && (
        <>
          <div className="pricing-feature-panels">
            <div className="pricing-feature-card">
              <h3>Payment Confirmation</h3>
              <p>
                During payment collection, successful authorization or plan updates are confirmed before your action resumes.
              </p>
            </div>
            <div className="pricing-feature-card">
              <h3>Why choose each option</h3>
              <ul className="pricing-inline-list">
                <li>PAYG for one-off usage with no monthly commitment.</li>
                <li>Plans for frequent usage with higher included limits.</li>
              </ul>
            </div>
          </div>

          <h2 className="pricing-section-title">Subscription Plans</h2>
          <div className="pricing-grid">
            {catalog.plans.length === 0 && (
              <div className="pricing-card">No active plans are available right now.</div>
            )}
            {catalog.plans.map((plan) => (
              <div className="pricing-card" key={plan.id}>
                <h3>
                  {plan.name}
                  {plan.isRecommended && <span className="pricing-recommended">Recommended</span>}
                </h3>
                <p className="pricing-price">{formatPlanPrice(plan)} / month</p>
                <ul className="pricing-list">
                  {plan.limits.slice(0, 6).map((limit) => (
                    <li key={`${plan.id}-${limit.featureKey}`}>
                      {limit.label}: {limit.maxPerMonth === null ? 'Unlimited' : limit.maxPerMonth}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="billing-btn pricing-select"
                  onClick={() => selectOption({ kind: 'plan', planId: plan.id })}
                >
                  Select {plan.name}
                </button>
              </div>
            ))}
          </div>

          <h2 className="pricing-section-title">Pay As You Go Rates</h2>
          <div className="pricing-table-wrap">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Rate</th>
                  <th>Monthly Cap</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {catalog.payg.map((item) => (
                  <tr key={item.featureKey}>
                    <td>{item.label}</td>
                    <td>{item.currency === 'INR' ? `₹${(item.price ?? item.pricePerUnit).toFixed(2)}` : formatPrice(item.price ?? item.pricePerUnit)} / {item.unit}</td>
                    <td>{item.maxPerMonth === null ? 'No cap' : item.maxPerMonth}</td>
                    <td>{new Date(item.updatedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="billing-btn pricing-select pricing-select-payg"
            onClick={() => selectOption({ kind: 'payg' })}
          >
            Choose Pay As You Go
          </button>
          <p className="pricing-note">All estimates are generated against UTC monthly periods.</p>
        </>
      )}
    </div>
  );
};

export default PricingPage;
